import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { 
  Sparkles,
  Send,
  Clock,
  Image as ImageIcon,
  Hash,
  Lightbulb,
  Wand2,
  CalendarIcon,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Loader2,
  Copy,
  Check,
  FileText,
  Layout
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ComposerPage = ({ user, logout, token }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [content, setContent] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("12:00");
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);

  const platformOptions = [
    { id: "twitter", name: "Twitter", icon: <Twitter className="w-4 h-4" />, color: "bg-blue-500" },
    { id: "instagram", name: "Instagram", icon: <Instagram className="w-4 h-4" />, color: "bg-pink-500" },
    { id: "facebook", name: "Facebook", icon: <Facebook className="w-4 h-4" />, color: "bg-indigo-500" },
    { id: "linkedin", name: "LinkedIn", icon: <Linkedin className="w-4 h-4" />, color: "bg-sky-500" },
    { id: "tiktok", name: "TikTok", icon: <span className="text-xs font-bold">TT</span>, color: "bg-gray-800" },
    { id: "youtube", name: "YouTube", icon: <Youtube className="w-4 h-4" />, color: "bg-red-500" },
    { id: "pinterest", name: "Pinterest", icon: <span className="text-xs font-bold">P</span>, color: "bg-red-700" }
  ];

  const categoryIcons = {
    promotional: "🎯",
    educational: "📚",
    engagement: "💬",
    storytelling: "📖",
    announcement: "📢",
    question: "❓"
  };

  useEffect(() => {
    fetchTemplates();
    if (editId) {
      fetchPost();
    }
  }, [editId, token]);

  const fetchTemplates = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/templates`, { headers }),
        axios.get(`${BACKEND_URL}/api/templates/categories`, { headers })
      ]);
      setTemplates(templatesRes.data);
      setTemplateCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/posts/${editId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const post = response.data;
      setContent(post.content);
      setPlatforms(post.platforms || []);
      setHashtags(post.hashtags || []);
      setStatus(post.status);
      if (post.scheduled_at) {
        const date = new Date(post.scheduled_at);
        setScheduledDate(date);
        setScheduledTime(format(date, "HH:mm"));
      }
    } catch (error) {
      toast.error("Error al cargar el post");
    }
  };

  const togglePlatform = (platformId) => {
    setPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleAIGenerate = async (type) => {
    if (!content.trim() && type !== 'ideas') {
      toast.error("Escribe algo de contenido primero");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/generate`,
        {
          prompt: content || "Ideas de contenido para redes sociales",
          context: platforms.join(", ") || "redes sociales en general",
          type: type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiSuggestions({
        type,
        content: response.data.content
      });

      if (type === 'hashtags') {
        const newHashtags = response.data.content
          .split(/\s+/)
          .filter(tag => tag.startsWith('#'))
          .map(tag => tag.replace('#', ''));
        setHashtags(prev => [...new Set([...prev, ...newHashtags])]);
      }

      toast.success("¡Sugerencia generada!");
    } catch (error) {
      toast.error("Error al generar con IA");
      console.error(error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleOptimize = async () => {
    if (!content.trim()) {
      toast.error("Escribe contenido para optimizar");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/optimize`,
        {
          prompt: content,
          context: platforms.join(", ") || "redes sociales",
          type: "optimize"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiSuggestions({
        type: 'optimize',
        content: response.data.optimized_content
      });
      toast.success("¡Contenido optimizado!");
    } catch (error) {
      toast.error("Error al optimizar");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = () => {
    if (aiSuggestions?.type === 'optimize' || aiSuggestions?.type === 'caption') {
      setContent(aiSuggestions.content);
    } else if (aiSuggestions?.type === 'title') {
      const firstTitle = aiSuggestions.content.split('\n')[0];
      setContent(firstTitle + '\n\n' + content);
    }
    setAiSuggestions(null);
    toast.success("Sugerencia aplicada");
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(aiSuggestions?.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const applyTemplate = (template) => {
    setContent(template.content);
    setPlatforms(template.platforms || []);
    setHashtags(template.hashtags || []);
    setTemplatesDialogOpen(false);
    toast.success(`Plantilla "${template.name}" aplicada`);
  };

  const filteredTemplates = selectedCategory === "all" 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const handleSubmit = async (publishStatus) => {
    if (!content.trim()) {
      toast.error("El contenido no puede estar vacío");
      return;
    }

    if (platforms.length === 0) {
      toast.error("Selecciona al menos una plataforma");
      return;
    }

    setLoading(true);
    try {
      let scheduledAt = null;
      if (scheduledDate && publishStatus === 'scheduled') {
        const [hours, minutes] = scheduledTime.split(':');
        const scheduled = new Date(scheduledDate);
        scheduled.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        scheduledAt = scheduled.toISOString();
      }

      const postData = {
        content,
        platforms,
        hashtags,
        scheduled_at: scheduledAt,
        status: publishStatus
      };

      let postId = editId;
      
      if (editId) {
        await axios.put(
          `${BACKEND_URL}/api/posts/${editId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const response = await axios.post(
          `${BACKEND_URL}/api/posts`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        postId = response.data.post_id;
      }

      // If publishing now, call the publish endpoint
      if (publishStatus === 'published' && postId) {
        try {
          const publishResponse = await axios.post(
            `${BACKEND_URL}/api/social/publish`,
            {
              post_id: postId,
              platforms: platforms
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const results = publishResponse.data.results;
          const successCount = results.filter(r => r.status === 'success').length;
          
          if (successCount === results.length) {
            toast.success(`¡Publicado en ${successCount} redes sociales!`);
          } else if (successCount > 0) {
            toast.warning(`Publicado en ${successCount}/${results.length} redes`);
          } else {
            toast.error("No se pudo publicar. Conecta tus cuentas de redes sociales.");
          }
        } catch (publishError) {
          console.error("Publish error:", publishError);
          const errorDetail = publishError.response?.data?.detail;
          if (errorDetail?.includes("No tienes cuentas conectadas")) {
            toast.error("Conecta tus cuentas de redes sociales en Cuentas → Cuentas");
          } else {
            toast.success(editId ? "Post actualizado (pendiente de publicar)" : "Post guardado (pendiente de publicar)");
          }
        }
      } else {
        toast.success(
          publishStatus === 'scheduled' ? "Post programado" :
          publishStatus === 'published' ? "Post guardado" : "Borrador guardado"
        );
      }
      
      navigate('/calendar');
    } catch (error) {
      toast.error("Error al guardar el post");
    } finally {
      setLoading(false);
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(prev => prev.filter(t => t !== tag));
  };

  const addHashtag = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim().replace('#', '');
      if (!hashtags.includes(newTag)) {
        setHashtags(prev => [...prev, newTag]);
      }
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                {editId ? 'Editar Post' : 'Crear Post'}
              </h1>
              <p className="text-muted-foreground mt-1">
                Crea y programa tu contenido con ayuda de IA
              </p>
            </div>
            <Dialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="templates-btn">
                  <Layout className="w-4 h-4 mr-2" />
                  Plantillas
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle>Plantillas de Contenido</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Category Filter */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedCategory === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory("all")}
                    >
                      Todas
                    </Button>
                    {templateCategories.map((cat) => (
                      <Button
                        key={cat.id}
                        variant={selectedCategory === cat.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {categoryIcons[cat.id]} {cat.name}
                      </Button>
                    ))}
                  </div>
                  
                  {/* Templates Grid */}
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredTemplates.map((template) => (
                        <div 
                          key={template.template_id}
                          className="p-4 border border-border rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                          onClick={() => applyTemplate(template)}
                          data-testid={`template-${template.template_id}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{categoryIcons[template.category]}</span>
                            <h4 className="font-semibold">{template.name}</h4>
                            {template.is_system && (
                              <Badge variant="secondary" className="text-xs">Sistema</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                            {template.content}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {template.platforms?.slice(0, 3).map((platform) => (
                              <Badge key={platform} variant="outline" className="text-xs capitalize">
                                {platform}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Editor */}
            <div className="lg:col-span-2 space-y-6">
              {/* Content Editor */}
              <Card data-testid="content-editor">
                <CardHeader>
                  <CardTitle className="text-lg">Contenido</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="¿Qué quieres compartir hoy?"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="min-h-[200px] resize-none"
                    data-testid="content-textarea"
                  />
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{content.length} caracteres</span>
                    <span className={content.length > 280 ? 'text-destructive' : ''}>
                      {content.length > 280 ? 'Muy largo para Twitter' : ''}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* AI Tools */}
              <Card data-testid="ai-tools">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Asistente IA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => handleAIGenerate('hashtags')}
                      disabled={aiLoading}
                      data-testid="ai-hashtags-btn"
                    >
                      <Hash className="w-4 h-4 mr-2" />
                      Hashtags
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleAIGenerate('title')}
                      disabled={aiLoading}
                      data-testid="ai-title-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Títulos
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleAIGenerate('ideas')}
                      disabled={aiLoading}
                      data-testid="ai-ideas-btn"
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Ideas
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleOptimize}
                      disabled={aiLoading}
                      data-testid="ai-optimize-btn"
                    >
                      {aiLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Optimizar
                    </Button>
                  </div>

                  {/* AI Suggestions */}
                  {aiSuggestions && (
                    <div className="mt-4 p-4 bg-secondary/50 rounded-lg border border-border" data-testid="ai-suggestions">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-primary">
                          Sugerencia de IA ({aiSuggestions.type})
                        </span>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={copyToClipboard}
                          >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          {(aiSuggestions.type === 'optimize' || aiSuggestions.type === 'caption' || aiSuggestions.type === 'title') && (
                            <Button 
                              size="sm"
                              onClick={applyAISuggestion}
                            >
                              Aplicar
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{aiSuggestions.content}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hashtags */}
              <Card data-testid="hashtags-section">
                <CardHeader>
                  <CardTitle className="text-lg">Hashtags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {hashtags.map((tag) => (
                      <Badge 
                        key={tag} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-destructive/20"
                        onClick={() => removeHashtag(tag)}
                      >
                        #{tag} ×
                      </Badge>
                    ))}
                  </div>
                  <Input 
                    placeholder="Escribe un hashtag y presiona Enter"
                    onKeyDown={addHashtag}
                    data-testid="hashtag-input"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Platforms */}
              <Card data-testid="platforms-section">
                <CardHeader>
                  <CardTitle className="text-lg">Plataformas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {platformOptions.map((platform) => (
                      <div 
                        key={platform.id}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox 
                          id={platform.id}
                          checked={platforms.includes(platform.id)}
                          onCheckedChange={() => togglePlatform(platform.id)}
                          data-testid={`platform-${platform.id}`}
                        />
                        <label 
                          htmlFor={platform.id}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <span className={`p-1.5 rounded text-white ${platform.color}`}>
                            {platform.icon}
                          </span>
                          <span className="font-medium">{platform.name}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Schedule */}
              <Card data-testid="schedule-section">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Programar
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        data-testid="date-picker-btn"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {scheduledDate 
                          ? format(scheduledDate, "PPP", { locale: es })
                          : "Seleccionar fecha"
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={scheduledDate}
                        onSelect={setScheduledDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <div>
                    <Label>Hora</Label>
                    <Input 
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="time-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card data-testid="actions-section">
                <CardContent className="pt-6 space-y-3">
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={() => handleSubmit('draft')}
                    disabled={loading}
                    data-testid="save-draft-btn"
                  >
                    Guardar Borrador
                  </Button>
                  <Button 
                    className="w-full"
                    onClick={() => handleSubmit('scheduled')}
                    disabled={loading || !scheduledDate}
                    data-testid="schedule-btn"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Programar
                  </Button>
                  <Button 
                    className="w-full gradient-neural glow-neural text-white"
                    onClick={() => handleSubmit('published')}
                    disabled={loading}
                    data-testid="publish-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Publicar Ahora
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
